import { useEffect, useRef, useState } from 'react'
import {
  inspectCurrentPolicy,
  type CurrentPolicyObservation,
} from '@/services/platform/policyGovernance'

export function CurrentPolicyPanel() {
  const [policyId, setPolicyId] = useState('')
  const [hash, setHash] = useState('')
  const [view, setView] = useState<CurrentPolicyObservation | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const generation = useRef(0)
  useEffect(() => {
    generation.current++
    setView(null)
    setError('')
  }, [policyId, hash])
  useEffect(() => {
    if (!view) return
    const timer = setTimeout(
      () => {
        setView(null)
        setError('Observation expired. Check the policy again.')
      },
      Math.max(
        0,
        Math.min(Date.parse(view.expires_at), Date.parse(view.checked_at) + 30000) - Date.now()
      )
    )
    return () => clearTimeout(timer)
  }, [view])
  useEffect(
    () => () => {
      generation.current++
    },
    []
  )
  async function inspect() {
    if (busy) return
    const request = ++generation.current
    setView(null)
    setError('')
    setBusy(true)
    try {
      const result = await inspectCurrentPolicy(policyId, hash)
      if (request === generation.current) setView(result)
    } catch {
      if (request === generation.current)
        setError(
          'No current observation is available. Check the policy ID, fingerprint, current administrator session and fresh MFA. A historical approval may have expired or been retired.'
        )
    } finally {
      setBusy(false)
    }
  }
  return (
    <section className="card p-5 space-y-3">
      <h2 className="text-lg font-semibold">Inspect current policy</h2>
      <p>
        Check the exact approved version. This observation does not appoint a controller;the policy
        may change after it is checked.
      </p>
      <label className="block text-sm">
        Policy to inspect
        <input
          className="input w-full"
          value={policyId}
          onChange={e => setPolicyId(e.target.value)}
        />
      </label>
      <label className="block text-sm">
        Policy body fingerprint
        <input className="input w-full" value={hash} onChange={e => setHash(e.target.value)} />
      </label>
      <button className="btn" disabled={busy} onClick={() => void inspect()}>
        Check current policy
      </button>
      {error && <p role="alert">{error}</p>}
      {view && (
        <div className="space-y-3" aria-label="Current policy observation">
          <p>
            {view.policy_key} · version {view.version}
          </p>
          <p className="break-all">Policy: {view.policy_id}</p>
          <p>
            Active when checked: {view.checked_at} · Expiry: {view.expires_at}
          </p>
          {view.body.rules.map(rule => (
            <article key={rule.rule_id} className="border rounded p-3 space-y-1">
              <h3 className="font-semibold">{rule.rule_id}</h3>
              <p>
                {rule.subject_type} · {rule.capacity} · {rule.controller_role}
              </p>
              <p>Allowed actions: {rule.allowed_actions.join(', ')}</p>
              <p>Required evidence: {rule.required_evidence_types.join(', ')}</p>
              <p>
                Maximum authority: {rule.maximum_authority_seconds} seconds · Independent acceptance
                required
              </p>
            </article>
          ))}
        </div>
      )}
    </section>
  )
}
